import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Confetti from 'react-confetti'
import SVGIcon from '@/components/icon'
import Tabs from '@/shared/Tabs'
import Loading from '@/shared/Loading'
import { MobileAdBundle, SidebarAdBundle } from '@/Layout/Ads'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Card from '@/shared/Card'
import Pagination from '@/shared/Pagination'
import UserHoverCard from '@/shared/UserHoverCard'

const NoResults = () => {
  return (
    <span>
      <div className="relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccblue" >
        <SVGIcon name='icon-messages' className='mx-auto h-7 w-7 sm:h-12 sm:w-12' />
        <span className="mt-2 block font-medium text-gray-900 dark:text-gray-300"> No notifications.</span>
      </div>
    </span>
  )
}
const Congratulations = () => {
  return (
    <span>
      <div className="relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccblue" >
        <SVGIcon name='icon-messages' className='mx-auto h-7 w-7 sm:h-12 sm:w-12' />
        <span className="mt-2 block font-medium text-gray-900 dark:text-gray-300"> You read everything! Time for a nap?</span>
      </div>
    </span>
  )
}

const AlertsIndex = () => {
  const { getRequest, putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const [, setCurrentUser] = useGlobalState('currentUser')
  const [loading, setLoading] = useState(true)
  const [readcount, setReadcount] = useState(0)
  const [clickedMarkAllAsRead, setClickedMarkAllAsRead] = useState(false)
  const [status, setStatus] = useState('unread')
  const [alerts, setAlerts] = useState([])
  const [meta, setMeta] = useState({})
  const [page, setPage] = useState(1)
  const statuses = ['unread', 'read']

  const changeTab = (tab) => { setPage(1); setStatus(tab) }
  const tablist = [
    { name: 'Unread', count: null, changeValue: 'unread' },
    { name: 'Archive', count: null, changeValue: 'read' }
  ]

  const fetchData = () => {
    setLoading(true)
    const data = { status: status, page: page }
    getRequest('/api/v3/alerts', data, (err, jsonData) => {
      setLoading(false)
      if (err) { /* hooks */ return }

      setAlerts(jsonData.alerts)
      setMeta(jsonData.meta || {})
    })
  }

  const updateReadcount = () => { setReadcount(readcount + 1) }

  const markAllRead = () => {
    setAlerts([])
    setClickedMarkAllAsRead(true)
    // Nothing left to read, so the bell goes dark with the list.
    setCurrentUser((u) => (u ? { ...u, notificationsCount: 0 } : u))
    putpostRequest(`/api/v3/alerts/mark_all_as_read`, 'POST', {}, (err, jsonData) => {
      if (err) { /* hooks */ }
    })
  }

  useEffect(() => { fetchData() }, [status, page])

  return <>
    { status === 'unread' && readcount === alerts.length && readcount > 0 && <Confetti style={{ position: 'fixed' }} width={window.innerWidth} gravity={0.05} colors={['#f7784e', '#00b0e7', '#6452a2']} numberOfPieces={300} recycle={false} height={window.innerHeight} /> }
    { clickedMarkAllAsRead && <Confetti style={{ position: 'fixed' }} width={window.innerWidth} gravity={0.05} colors={['#f7784e', '#00b0e7', '#6452a2']} numberOfPieces={300} recycle={false} height={window.innerHeight} /> }
    <MobileAdBundle ezoicAdId={109} />
    <div className='lg:block lg:flex lg:gap-x-2'>
      <div id='notifications' className='max-w-6xl w-full'>
        <Card title={'Notifications'} header={
          <span className='text-cccorange cursor-pointer bg-white px-2 py-1 rounded-md' onClick={markAllRead}>Mark all as Read</span>
        }>
          <>
            { loading && <Loading /> }
            <Tabs statuses={statuses} tablist={tablist} currentTab={status} changeTab={changeTab} />
            <div className="flex flex-col gap-y-1">
              {alerts.map((alert) => <AlertRow key={`alert${alert.id}`} alert={alert} status={status} updateReadcount={updateReadcount} />)}
            </div>
            {alerts.length === 0 && !clickedMarkAllAsRead && <NoResults />}
            {alerts.length > 0 && meta.totalPages > 1 && <div className='mt-2'><Pagination meta={meta} callback={setPage} scrollTop /></div>}
            { status === 'unread' && readcount === alerts.length && readcount > 0 && <Congratulations /> }
            { clickedMarkAllAsRead && <Congratulations /> }
          </>
        </Card>
      </div>
      <SidebarAdBundle ezoicAdId={108} />
    </div>
  </>
}

const AlertRow = (props) => {
  const { alert, status, updateReadcount } = props
  const [read, setRead] = useState(alert.read)
  const [, setCurrentUser] = useGlobalState('currentUser')
  const { putpostRequest } = useQuery()

  const markRead = () => {
    setRead(true)
    updateReadcount()
    // The server decrements notifications_count (Alert#update_user_counter),
    // but the bell badge reads the copy in global state, so it stayed lit
    // until the next full page load. PopupAlert adds one on the way in; this
    // takes one back off.
    setCurrentUser((u) => (u ? { ...u, notificationsCount: Math.max((u.notificationsCount || 0) - 1, 0) } : u))
    putpostRequest(`/api/v3/alerts/${alert.id}/mark_as_read`, 'POST', {}, (err, jsonData) => {
      if (err) { /* hooks */ }
    })
  }
  if (status === 'unread' && read) { return null }

  return <div className={`${read ? 'bg-white dark:bg-gray-700' : 'bg-cccblue bg-opacity-10'} rounded-lg border border-gray-200 dark:border-gray-700 p-1`}>
    <div className='flex flex-col sm:flex-row justify-between'>
      { alert.data.type === 'clc_education_synced' && <ClcEducationSynced alert={alert} /> }
      { alert.data.type === 'message_from_buford' && <MessageFromBuford alert={alert} /> }
      { alert.data.type === 'new_audition_on_your_project' && <NewAuditionOnYourProject alert={alert} /> }
      { alert.data.type === 'new_recommendation' && <NewRecommendation alert={alert} /> }
      { alert.data.type === 'new_coins_gift' && <NewCoinsGift alert={alert} /> }
      { alert.data.type === 'new_coins' && <NewCoins alert={alert} /> }
      { alert.data.type === 'new_follower_project' && <NewFollowerProject alert={alert} /> }
      { alert.data.type === 'following_user_assigned_to_role' && <FollowingUserAssignedToRole alert={alert} /> }
      { alert.data.type === 'removed_from_role' && <RemovedFromRole alert={alert} /> }
      { alert.data.type === 'assigned_to_role' && <AssignedToRole alert={alert} /> }
      { alert.data.type === 'shortlisted' && <Shortlisted alert={alert} /> }
      { alert.data.type === 'new_audition_comment' && <NewAuditionComment alert={alert} /> }
      { alert.data.type === 'new_project_comment' && <NewProjectComment alert={alert} /> }
      { alert.data.type === 'new_project_reply' && <NewProjectComment alert={alert} reply /> }
      { alert.data.type === 'new_audition_reply' && <NewAuditionComment alert={alert} reply /> }
      { alert.data.type === 'new_follower' && <NewFollower alert={alert} /> }
      { alert.data.type === 'new_project_follower' && <NewProjectFollower alert={alert} /> }
      { alert.data.type === 'new_mention' && <NewMention alert={alert} /> }
      { alert.data.type === 'new_upvoted_audition' && <NewUpvotedAudition alert={alert} /> }
      { alert.data.type === 'successful_audition_upload' && <SuccessfulAuditionUpload alert={alert} /> }
      { alert.data.type === 'upload_failed' && <UploadFailed alert={alert} /> }
      { alert.data.type === 'new_user_credit' && <NewUserCredit alert={alert} /> }
      { alert.data.type === 'new_social_reply' && <NewSocialReply alert={alert} /> }
      { alert.data.type === 'project_collaborator_invite' && <ProjectCollaboratorInvite alert={alert} /> }
      { alert.data.type === 'project_collaborator_accepted' && <ProjectCollaboratorAccepted alert={alert} /> }
      { alert.data.type === 'feedback_complete' && <FeedbackComplete alert={alert} /> }
      { alert.data.type === 'project_updates' && <FollowedProjects alert={alert} verb='posted an update' /> }
      { alert.data.type === 'project_end' && <FollowedProjects alert={alert} verb='wrapped up' /> }
      { alert.data.type === 'role_almost_expired' && <RoleAlmostExpired alert={alert} /> }
      <div className="mt-0.5 text-sm text-gray-800 dark:text-gray-200 flex sm:flex-col gap-2 justify-between">
        <span className='flex justify-end'>{alert.timeAgo}</span>
        { !read && <span className='text-cccorange cursor-pointer bg-white px-2 py-1 rounded-md' onClick={markRead}>Mark as Read</span> }
      </div>
    </div>
  </div>
}

// A Closing Credits cohort wrapped up and landed on this person's profile as
// a verified education row. Links to the edit page rather than the public
// profile so the badge and the delete button are both in reach.
const ClcEducationSynced = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">Look at you, a scholar. <b>{alert.data.course_name}</b> from Closing Credits is now on your profile. I even gave it a verified badge.</p>
      <a href={alert.data.url || '/profile/work'} className="font-medium text-cccblue cursor-pointer truncate">Go admire it →</a>
    </div>
  </div>
}

// A note Buford typed by hand — account status, rate limits, "you're
// unbanned". Styled deliberately unlike the automated rows (orange rule,
// name badge, message rendered verbatim) so it reads as a person wrote it
// and not a cron job. whitespace-pre-line keeps his paragraph breaks.
const MessageFromBuford = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1 border-l-4 border-cccorange pl-3">
      <div className='flex items-center gap-x-2'>
        <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-cccorange text-white'>Buford</span>
        <span className='text-xs text-gray-500 dark:text-gray-400'>Casting Call Club HQ</span>
      </div>
      <p className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">{alert.data.message}</p>
      { alert.data.url && <a href={alert.data.url} className="font-medium text-cccblue cursor-pointer truncate">
        {alert.data.link_text || 'Have a look'} →
      </a> }
    </div>
  </div>
}

// A feature request the user voted on got built. Mobile deep-links the same
// slug, so keep the href shape in step with src/push/deepLink.ts over there.
const FeedbackComplete = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">You voted for <b>{alert.data.feedback_title}</b> and I went and built it.</p>
      <a href={`/feedback/${alert.data.feedback_slug}`} className="font-medium text-cccblue cursor-pointer truncate">
        See what shipped
      </a>
    </div>
  </div>
}

const NewUserCredit = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">You were credited for <b>{alert.data.role_name}</b> in <b>{alert.data.credit_name}</b>! Nice!</p>
      </div>
      <a href={`/credits`} className="font-medium text-cccblue cursor-pointer truncate">
        Approve or Deny It
      </a>
    </div>
  </div>
}

const UploadFailed = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">Your <b>{alert.data.kind}</b> upload failed. {JSON.stringify(alert.data.errors)}</p>
      </div>
      <span className="text-xs text-gray-800 dark:text-gray-200"> Please try again.</span>
    </div>
  </div>
}

const SuccessfulAuditionUpload = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">Your upload for <b>{alert.data.role_name}</b> was successfully submitted. The project owner will review it.</p>
      </div>
      <a href={`/submissions/${alert.data.audition_id}`} className="font-medium text-cccblue cursor-pointer truncate">
        View Submission
      </a>
      <span className="text-xs text-gray-800 dark:text-gray-200"> You received this notification because you enabled it in your Settings </span>
    </div>
  </div>
}

const NewUpvotedAudition = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">Your submission for role <b>{alert.data.role_name}</b> now has <b>{alert.data.total_upvotes}</b> upvotes!</p>
      </div>
      <a href={`/submissions/${alert.data.audition_id}`} className="font-medium text-cccblue cursor-pointer truncate">
        View Submission
      </a>
    </div>
  </div>
}

const NewMention = (props) => {
  const { alert } = props
  if (alert.data.object_type === 'PostMessage') {
    return <div className="relative flex items-start space-x-3 overflow-hidden">
      <div className="min-w-0 flex-1">
        <div className='flex flex-col'>
          <span className='text-gray-800 dark:text-gray-200'>{alert.data.mentioner_display_name || 'Someone'} mentioned you in a message.</span>
          { alert.data.preview && <span className='text-sm text-gray-500 dark:text-gray-400 truncate'>"{alert.data.preview}"</span> }
          <a href={`/private_messages/${alert.data.post_id}`} className="font-medium text-cccblue cursor-pointer truncate"> View message </a>
        </div>
      </div>
    </div>
  }
  let url
  if (alert.data.object_type === 'Audition') { url = `/submissions/${alert.data.object_id}` }
  if (alert.data.object_type === 'Project') { url = `/projects/${alert.data.object_id}` }
  if (alert.data.object_type === 'Comment' && alert.data.commentable_type === 'Project') { url = `/projects/${alert.data.object_id}` }
  if (alert.data.object_type === 'Comment' && alert.data.commentable_type === 'Audition') { url = `/submissions/${alert.data.object_id}` }
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex gap-x-1'>
          <span className='text-gray-800 dark:text-gray-200'>You were mentioned in a comment.</span>
        </div>
        { url && <a href={url} className="font-medium text-cccblue cursor-pointer truncate"> View comment </a> }
      </div>
    </div>
  </div>
}

const NewProjectFollower = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <UserHoverCard username={alert.data.follower_username} displayName={alert.data.follower_display_name} />
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">started following your project {alert.data.project_name}. </p>
      </div>
    </div>
  </div>
}

const NewFollower = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <UserHoverCard username={alert.data.follower_username} displayName={alert.data.follower_display_name} />
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">started following you. Maybe you should follow them back! </p>
      </div>
      <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">See all your followers on your public profile.</p>
    </div>
  </div>
}

const NewProjectComment = (props) => {
  const { alert, reply } = props
  const url = `/projects/${alert.data.project_id}`
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex gap-x-1'>
          <span className='text-gray-800 dark:text-gray-200'><b>{alert.data.comment_user_display_name}</b> {reply ? 'replied to your comment on' : 'commented on'} your project.</span>
        </div>
        <a href={url} className="font-medium text-cccblue cursor-pointer truncate"> {alert.data.project_name} </a>
      </div>
    </div>
  </div>
}

// Notification for the invitee: someone invited them to collaborate on a
// project. Links to /manage/projects where ManageProjectsIndex renders
// the pending-invite list with Accept/Reject buttons.
const ProjectCollaboratorInvite = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex flex-col sm:flex-row gap-x-1'>
          <UserHoverCard username={alert.data.inviter_username} displayName={alert.data.inviter_display_name} />
          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">invited you to collaborate on <b>{alert.data.project_name}</b>.</p>
        </div>
        <a href='/manage/projects' className="font-medium text-cccblue cursor-pointer truncate">Accept or decline the invite →</a>
      </div>
    </div>
  </div>
}

// Notification for the owner: someone they invited has accepted. Links
// straight to the project's team management page.
const ProjectCollaboratorAccepted = (props) => {
  const { alert } = props
  const url = `/manage/projects/${alert.data.project_id}/team`
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex flex-col sm:flex-row gap-x-1'>
          <UserHoverCard username={alert.data.invitee_username} displayName={alert.data.invitee_display_name} />
          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">accepted your invite to collaborate on <b>{alert.data.project_name}</b>. Welcome them aboard!</p>
        </div>
        <a href={url} className="font-medium text-cccblue cursor-pointer truncate">View team →</a>
      </div>
    </div>
  </div>
}

const NewAuditionComment = (props) => {
  const { alert, reply } = props
  const url = `/submissions/${alert.data.audition_id}`
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex gap-x-1'>
          <span className='text-gray-800 dark:text-gray-200'><b>{alert.data.comment_user_display_name}</b> {reply ? 'replied to your comment on' : 'commented on'} your submission.</span>
        </div>
        <a href={url} className="font-medium text-cccblue cursor-pointer truncate"> {alert.data.role_name} </a>
      </div>
    </div>
  </div>
}

const AssignedToRole = (props) => {
  const { alert } = props
  const url = `/projects/${alert.data.project_id}`
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="relative m-1">
      <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccorange" src={alert.data.image_url} alt="" />
    </div>
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex gap-x-1'>
          <span className='text-gray-800 dark:text-gray-200'>You were assigned to role: <b>{alert.data.name}</b></span>
        </div>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">Congratulations! The project creator will be in touch with you shortly with some instructions on what to do next.</p>
        <a href={url} className="font-medium text-cccblue cursor-pointer truncate"> {alert.data.project_name} </a>
      </div>
    </div>
  </div>
}

const Shortlisted = (props) => {
  const { alert } = props
  const url = `/projects/${alert.data.project_id}`
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="relative m-1">
      <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccblue" src={alert.data.image_url} alt="" />
    </div>
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex gap-x-1'>
          <span className='text-gray-800 dark:text-gray-200'>So close! You were shortlisted for: <b>{alert.data.name}</b></span>
        </div>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">They narrowed it down to a chosen few and you made the cut. Keep an eye out for a message from them, they might want more.</p>
        <a href={url} className="font-medium text-cccblue cursor-pointer truncate"> {alert.data.project_name} </a>
      </div>
    </div>
  </div>
}

const RemovedFromRole = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">You were removed from role <b>{alert.data.name}</b> </p>
      </div>
    </div>
  </div>
}

const FollowingUserAssignedToRole = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200"><b>{alert.data.count} people</b> were all assigned to a role today: </p>
        <i className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">{alert.data.ten_display_names.join(', ')}</i>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">Be sure to congratulate them!</p>
        <span className="text-xs text-gray-800 dark:text-gray-200"> You received this notification because you follow these people </span>
      </div>
    </div>
  </div>
}

const NewFollowerProject = (props) => {
  const { alert } = props
  const url = `/projects/${alert.data.project_id}`
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="relative m-1">
      <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccorange" src={alert.data.image_url} alt="" />
    </div>
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex flex-col sm:flex-row gap-x-1'>
          <UserHoverCard username={alert.data.username} displayName={alert.data.display_name} />
          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">posted a new project.</p>
        </div>
        <a href={url} className="font-medium text-cccblue cursor-pointer truncate"> {alert.data.project_name} </a>
        <span className="text-xs text-gray-800 dark:text-gray-200"> You received this notification because you follow this person </span>
      </div>
    </div>
  </div>
}

// Daily bundles for things the user follows. `projects` / `roles` hold at
// most five; `count` is the real total.
const FollowedProjects = (props) => {
  const { alert, verb } = props
  const projects = alert.data.projects || []
  const more = (alert.data.count || projects.length) - projects.length
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">
        {projects.length === 1 ? 'A project you follow' : `${alert.data.count} projects you follow`} {verb}.
      </p>
      {projects.map((p) => <a key={p.id} href={`/projects/${p.id}`} className="block font-medium text-cccblue cursor-pointer truncate">{p.name}</a>)}
      {more > 0 && <span className='text-xs text-gray-500 dark:text-gray-400'>and {more} more</span>}
    </div>
  </div>
}

const RoleAlmostExpired = (props) => {
  const { alert } = props
  const roles = alert.data.roles || []
  const more = (alert.data.count || roles.length) - roles.length
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">
        {roles.length === 1 ? 'A role you favorited closes' : `${alert.data.count} roles you favorited close`} in about a day. Last call.
      </p>
      {roles.map((r) => <a key={r.id} href={`/projects/${r.project_id}`} className="block font-medium text-cccblue cursor-pointer truncate">{r.name} in {r.project_name}</a>)}
      {more > 0 && <span className='text-xs text-gray-500 dark:text-gray-400'>and {more} more</span>}
    </div>
  </div>
}

const NewCoins = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="min-w-0 flex-1">
      <div className='flex flex-col gap-y-2'>
        <div className="text-sm text-gray-800 dark:text-gray-200">You just got <b>{alert.data.amount}</b> cccCoins</div>
        <div className="text-sm text-gray-800 dark:text-gray-200">{alert.data.reason}</div>
      </div>
      <a href='/coins' className="font-medium text-cccblue cursor-pointer truncate">
        See your cccCoins
      </a>
    </div>
  </div>
}

const NewCoinsGift = (props) => {
  const { alert } = props
  let url
  if (alert.data.giftable_type === 'Audition') { url = `/submissions/${alert.data.giftable_id}` }
  if (alert.data.giftable_type === 'Project') { url = `/projects/${alert.data.giftable_id}` }
  if (alert.data.giftable_type === 'Comment') { url = null }
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="relative m-1">
      <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccorange" src={alert.data.image_url} alt="" />
    </div>
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex gap-x-1'>
          <span className='text-gray-800 dark:text-gray-200'>{alert.data.sender_username}</span>
          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">has awarded you <b>{alert.data.gift_name}</b></p>
        </div>
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">{alert.data.message}</p>
        { url && <a href={url} className="font-medium text-cccblue cursor-pointer truncate"> View Award </a> }
        <i className="text-xs text-gray-800 dark:text-gray-200"> That's so nice of them </i>
      </div>
    </div>
  </div>
}

const NewRecommendation = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="relative m-1">
      <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccorange" src={alert.data.image_url} alt="" />
    </div>
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <UserHoverCard username={alert.data.giver_username} displayName={alert.data.giver_displayname} />
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">gave you a recommendation </p>
      </div>
      <a href='/recommendations' className="font-medium text-cccblue cursor-pointer truncate">
        Approve or Deny It
      </a>
    </div>
  </div>
}

const NewAuditionOnYourProject = (props) => {
  const { alert } = props
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="relative m-1">
      <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccorange" src={alert.data.project_image_url} alt="" />
    </div>
    <div className="min-w-0 flex-1">
      <div className='flex flex-col sm:flex-row gap-x-1'>
        <UserHoverCard username={alert.data.audition_username} displayName={alert.data.audition_display_name} />
        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">entered a submission to your project </p>
      </div>
      <a href={`/submissions/${alert.data.audition_id}`} className="font-medium text-cccblue cursor-pointer truncate">
        {alert.data.project_name}
      </a>
    </div>
  </div>
}

const NewSocialReply = (props) => {
  const { alert } = props
  const url = `/social?post_id=${alert.data.social_post_id}`
  return <div className="relative flex items-start space-x-3 overflow-hidden">
    <div className="relative m-1">
      <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccorange" src={alert.data.reply_user_image_url} alt="" />
    </div>
    <div className="min-w-0 flex-1">
      <div className='flex flex-col'>
        <div className='flex gap-x-1'>
          <UserHoverCard username={alert.data.reply_username} displayName={alert.data.reply_display_name} />
          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">replied to your social post</p>
        </div>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 italic">"{alert.data.post_content_preview}"</p>
        <a href={url} className="font-medium text-cccblue cursor-pointer truncate">
          View Reply
        </a>
      </div>
    </div>
  </div>
}

AlertRow.propTypes = {
  alert: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
  updateReadcount: PropTypes.func.isRequired
}
NewAuditionOnYourProject.propTypes = { alert: PropTypes.object.isRequired }
NewRecommendation.propTypes = { alert: PropTypes.object.isRequired }
NewCoinsGift.propTypes = { alert: PropTypes.object.isRequired }
NewSocialReply.propTypes = { alert: PropTypes.object.isRequired }
MessageFromBuford.propTypes = { alert: PropTypes.object.isRequired }

export default AlertsIndex
